import { useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { User, Phone, Mail, MapPin, Calendar, Building2, Hash, Edit3, Save, X, Camera, Upload } from "lucide-react";

const CLOUD_NAME    = "dan0bapfg";
const UPLOAD_PRESET = "htst_event";
const MAX_SIZE_KB   = 250;

const ROLE_COLORS = {
  superadmin: "#f59e0b",
  admin:      "#3b82f6",
  member:     "#22c55e",
  volunteer:  "#a855f7",
  intern:     "#ec4899",
};

export default function Profile() {
  const { currentUser, userProfile, updateUserProfile } = useAuth();
  const [editing,    setEditing]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [photoFile,  setPhotoFile]  = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name:       userProfile?.name       || "",
    phone:      userProfile?.phone      || "",
    address:    userProfile?.address    || "",
    dob:        userProfile?.dob        || "",
    university: userProfile?.university || "",
    bio:        userProfile?.bio        || "",
  });

  const role = userProfile?.role || "member";
  const rc   = ROLE_COLORS[role] || "#22c55e";
  const name = userProfile?.name || currentUser?.email?.split("@")[0] || "Member";
  const currentPhoto = userProfile?.photoUrl || null;

  // ── Handle photo file selection ──
  function handlePhotoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please select an image file");
    if (file.size > MAX_SIZE_KB * 1024) {
      toast.error(`Max size is ${MAX_SIZE_KB}KB. This file is ${Math.round(file.size/1024)}KB`);
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  // ── Upload photo to Cloudinary ──
  async function handlePhotoUpload() {
    if (!photoFile) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file",          photoFile);
      formData.append("upload_preset", UPLOAD_PRESET);
      formData.append("folder",        `htst_members/${currentUser.uid}/photo`);

      const res  = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
        method:"POST", body:formData
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();

      await updateUserProfile({ photoUrl: data.secure_url });
      setPhotoFile(null);
      setPhotoPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Profile photo updated! ✅");
    } catch(e) {
      console.error(e);
      toast.error("Photo upload failed: " + e.message);
    } finally { setUploadingPhoto(false); }
  }

  // ── Save profile edits ──
  async function handleSave() {
    setSaving(true);
    try {
      await updateUserProfile(form);
      toast.success("Profile updated!");
      setEditing(false);
    } catch { toast.error("Failed to update profile"); }
    finally { setSaving(false); }
  }

  const INFO = [
    { icon:Mail,      label:"Email",          value:currentUser?.email,       key:null },
    { icon:Phone,     label:"Phone",          value:userProfile?.phone,       key:"phone" },
    { icon:Calendar,  label:"Date of Birth",  value:userProfile?.dob,         key:"dob" },
    { icon:MapPin,    label:"Address",        value:userProfile?.address,     key:"address" },
    { icon:Building2, label:"University/Org", value:userProfile?.university,  key:"university" },
    { icon:Hash,      label:"Member ID",      value:userProfile?.memberId,    key:null },
    { icon:Calendar,  label:"Joined On",      value:userProfile?.joinDate,    key:null },
  ];

  return (
    <div style={{ maxWidth:700 }}>

      {/* ── Profile Header ── */}
      <div style={s.headerCard}>
        <div style={{ display:"flex", alignItems:"center", gap:20, flexWrap:"wrap" }}>

          {/* Avatar with camera button */}
          <div style={{ position:"relative", flexShrink:0 }}>
            {currentPhoto || photoPreview ? (
              <img
                src={photoPreview || currentPhoto}
                alt={name}
                style={{ width:80, height:80, borderRadius:"50%", objectFit:"cover", border:`3px solid ${rc}` }}
              />
            ) : (
              <div style={{ width:80, height:80, borderRadius:"50%", background:`${rc}22`, border:`3px solid ${rc}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:32, fontWeight:800, color:rc }}>
                {name.charAt(0).toUpperCase()}
              </div>
            )}
            {/* Camera button */}
            <button
              onClick={() => fileRef.current?.click()}
              style={{ position:"absolute", bottom:0, right:0, width:26, height:26, borderRadius:"50%", background:"#f59e0b", border:"2px solid #111009", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
              <Camera size={13} color="#0a0908"/>
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handlePhotoChange} style={{ display:"none" }}/>
          </div>

          {/* Name + badges */}
          <div style={{ flex:1 }}>
            <h2 style={s.name}>{userProfile?.name || "Set your name"}</h2>
            <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:8 }}>
              <span style={{ ...s.rolePill, background:`${rc}18`, color:rc, border:`1px solid ${rc}33` }}>
                {role.toUpperCase()}
              </span>
              <span style={s.memberId}>{userProfile?.memberId || "HTS-0000"}</span>
              <span style={{ ...s.statusPill, background:userProfile?.status==="active"?"rgba(34,197,94,0.12)":"rgba(239,68,68,0.12)", color:userProfile?.status==="active"?"#22c55e":"#ef4444" }}>
                ● {userProfile?.status || "active"}
              </span>
            </div>
            {userProfile?.bio && <p style={s.bio}>{userProfile.bio}</p>}
          </div>

          {/* Edit button */}
          <button
            onClick={() => editing ? setEditing(false) : setEditing(true)}
            style={{ ...s.editBtn, background:editing?"rgba(239,68,68,0.1)":"rgba(245,158,11,0.1)", color:editing?"#ef4444":"#f59e0b", border:`1px solid ${editing?"rgba(239,68,68,0.3)":"rgba(245,158,11,0.3)"}` }}>
            {editing ? <><X size={15}/> Cancel</> : <><Edit3 size={15}/> Edit Profile</>}
          </button>
        </div>

        {/* Photo upload confirm bar */}
        {photoFile && (
          <div style={{ marginTop:16, padding:"12px 16px", background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.2)", borderRadius:10, display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
            <img src={photoPreview} alt="preview" style={{ width:40, height:40, borderRadius:"50%", objectFit:"cover", border:"2px solid #f59e0b" }}/>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:600, color:"#fef3e2" }}>New photo selected</div>
              <div style={{ fontSize:11, color:"#78716c" }}>{Math.round(photoFile.size/1024)}KB · Click upload to save</div>
            </div>
            <button onClick={handlePhotoUpload} disabled={uploadingPhoto}
              style={{ padding:"8px 16px", background:"#f59e0b", color:"#0a0908", border:"none", borderRadius:8, fontSize:12, fontWeight:800, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:6, opacity:uploadingPhoto?0.6:1 }}>
              <Upload size={13}/> {uploadingPhoto ? "Uploading..." : "Upload Photo"}
            </button>
            <button onClick={() => { setPhotoFile(null); setPhotoPreview(null); if(fileRef.current) fileRef.current.value=""; }}
              style={{ background:"none", border:"none", cursor:"pointer", color:"#78716c" }}>
              <X size={16}/>
            </button>
          </div>
        )}
      </div>

      {/* ── Edit Form ── */}
      {editing && (
        <div style={s.card}>
          <div style={s.cardTitle}>Edit Information</div>
          <div style={s.formGrid}>
            <Field label="Full Name"            value={form.name}       onChange={v=>setForm(f=>({...f,name:v}))}       placeholder="Your full name"/>
            <Field label="Phone Number"         value={form.phone}      onChange={v=>setForm(f=>({...f,phone:v}))}      placeholder="+91 XXXXX XXXXX"/>
            <Field label="Date of Birth"        value={form.dob}        onChange={v=>setForm(f=>({...f,dob:v}))}        placeholder="YYYY-MM-DD" type="date"/>
            <Field label="University / Org"     value={form.university} onChange={v=>setForm(f=>({...f,university:v}))} placeholder="Amity University"/>
            <div style={{ gridColumn:"1/-1" }}>
              <Field label="Address"            value={form.address}    onChange={v=>setForm(f=>({...f,address:v}))}    placeholder="City, State"/>
            </div>
            <div style={{ gridColumn:"1/-1" }}>
              <label style={s.label}>Short Bio</label>
              <textarea value={form.bio} onChange={e=>setForm(f=>({...f,bio:e.target.value}))}
                placeholder="Tell us about yourself..." rows={3}
                style={{ ...s.input, resize:"vertical", minHeight:80 }}/>
            </div>
          </div>
          <button onClick={handleSave} disabled={saving} style={{ ...s.saveBtn, opacity:saving?0.6:1 }}>
            <Save size={15}/> {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      )}

      {/* ── Personal Info ── */}
      <div style={s.card}>
        <div style={s.cardTitle}>Personal Information</div>
        <div style={s.infoGrid}>
          {INFO.map(({ icon:Icon, label, value }) => (
            <div key={label} style={s.infoItem}>
              <div style={s.infoIcon}><Icon size={15} color="#f59e0b"/></div>
              <div>
                <div style={s.infoLabel}>{label}</div>
                <div style={s.infoVal}>{value || <span style={{ color:"#52504d" }}>Not set</span>}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Account Details ── */}
      <div style={s.card}>
        <div style={s.cardTitle}>Account Details</div>
        {[
          ["User ID",         currentUser?.uid],
          ["Auth Provider",   "Email / Password"],
          ["Account Status",  userProfile?.status || "active"],
        ].map(([k,v]) => (
          <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
            <span style={{ fontSize:13, color:"#78716c" }}>{k}</span>
            <span style={{ fontSize:12, color:"#a8a29e", fontFamily:k==="User ID"?"monospace":"inherit", maxWidth:"60%", textAlign:"right", overflow:"hidden", textOverflow:"ellipsis" }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type="text" }) {
  return (
    <div>
      <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#78716c", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:6 }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width:"100%", padding:"10px 12px", background:"#0a0908", border:"1px solid rgba(255,255,255,0.1)", borderRadius:9, color:"#fef3e2", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}/>
    </div>
  );
}

const s = {
  headerCard: { background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:22, marginBottom:16 },
  name:       { fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:800, color:"#fef3e2", margin:"0 0 10px" },
  rolePill:   { fontSize:10, fontWeight:800, padding:"3px 10px", borderRadius:50, letterSpacing:"0.5px" },
  memberId:   { fontSize:12, color:"#78716c", fontWeight:700, fontFamily:"monospace" },
  statusPill: { fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:50 },
  bio:        { fontSize:13, color:"#78716c", marginTop:10, lineHeight:1.6 },
  editBtn:    { display:"inline-flex", alignItems:"center", gap:7, padding:"9px 18px", borderRadius:9, cursor:"pointer", fontSize:13, fontWeight:700, fontFamily:"inherit", transition:"all 0.2s", flexShrink:0 },
  card:       { background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20, marginBottom:16 },
  cardTitle:  { fontSize:14, fontWeight:700, color:"#fef3e2", marginBottom:16 },
  formGrid:   { display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:18 },
  label:      { display:"block", fontSize:11, fontWeight:700, color:"#78716c", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:6 },
  input:      { width:"100%", padding:"10px 12px", background:"#0a0908", border:"1px solid rgba(255,255,255,0.1)", borderRadius:9, color:"#fef3e2", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" },
  saveBtn:    { display:"inline-flex", alignItems:"center", gap:8, padding:"11px 24px", background:"#f59e0b", color:"#0a0908", border:"none", borderRadius:9, fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"inherit" },
  infoGrid:   { display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 },
  infoItem:   { display:"flex", alignItems:"flex-start", gap:10, background:"rgba(255,255,255,0.02)", borderRadius:9, padding:12 },
  infoIcon:   { width:30, height:30, background:"rgba(245,158,11,0.1)", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  infoLabel:  { fontSize:11, color:"#52504d", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:4 },
  infoVal:    { fontSize:13, color:"#fef3e2", fontWeight:600 },
};